import * as React from 'react';
import { Link } from 'react-router-dom';
import { Collapse, Container, Navbar, NavbarBrand, NavbarToggler, NavItem, NavLink } from 'reactstrap';
import AccountNavMenu from './account/AccountNavMenu';
import './NavMenu.css';
import ApplicationVersion from './settings/ApplicationVersion';

export default class NavMenu extends React.PureComponent<{}, { isOpen: boolean, isDarkTheme: boolean }> {

    /**
     * Key in local storage for the dark theme setting.
     */
    private isDarkThemeKey = "IsDarkTheme";
    private darkTheme = "Dark";
    private lightTheme = "Light";

    /**
     * The state of the component.
     */
    public state = {
        isOpen: false,
        isDarkTheme: this.isDarkTheme()
    };

    /**
     * Renders the NavMenu component.
     * @returns
     */
    public render() {
        // Set the theme based on local storage.
        this.setTheme(this.isDarkTheme());

        return (
            <header>
                <Navbar className="navbar-expand-sm navbar-toggleable-sm border-bottom box-shadow mb-3" dark>
                    <Container>
                        <NavbarBrand tag={Link} to="/">
                            <img className="brand-logo" alt="logo" src="./icons8-timesheet-32-white.png" />
                            <span>Timesheet</span>
                            <ApplicationVersion />
                        </NavbarBrand>
                        <NavbarToggler onClick={this.toggle} className="mr-2" />
                        <Collapse className="d-sm-inline-flex flex-sm-row-reverse" isOpen={this.state.isOpen} navbar>
                            <ul className="navbar-nav flex-grow">
                                <NavItem>
                                    <NavLink tag={Link} className="text-dark" to="#" onClick={this.toggleTheme}>
                                        {this.getThemeLinkText()}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink tag={Link} className="text-dark" to="/times">Times</NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink tag={Link} className="text-dark" to="/">Enter Time</NavLink>
                                </NavItem>
                                <AccountNavMenu />
                            </ul>
                        </Collapse>
                    </Container>
                </Navbar>
            </header>
        );
    }

    /**
     * Toggles the state of the NavMenu to open or close the menu.
     */
    private toggle = () => {
        this.setState({
            isOpen: !this.state.isOpen
        });
    }

    /**
     * Gets the text to display on the Theme link to switch between light/dark.
     * @returns The text to display on the link.
     */
    private getThemeLinkText = () => {
        const toggleThemeText = this.state.isDarkTheme ? this.lightTheme : this.darkTheme;
        const themeLinkText = `${toggleThemeText} Theme`;

        return themeLinkText;
    }

    /**
     * Toggles using the Light/Dark theme by adding or removing a CSS class
     * to the body tag using Bootstrap.
     */
    private toggleTheme = () => {
        const isDarkTheme = !this.isDarkTheme();
        this.setState({
            isDarkTheme: isDarkTheme
        });

        if (isDarkTheme) {
            this.setBooleanToLocalStorage(this.isDarkThemeKey, true);
        }
        else {
            this.setBooleanToLocalStorage(this.isDarkThemeKey, false);
        }
    }

    /**
     * Sets the theme based on the current state of the component.     
     */
    private setTheme = (isDarkTheme: Boolean) => {
        if (isDarkTheme) {
            // Turn on the dark theme.
            document.body.className = 'bootstrap-dark';
        }
        else {
            // Turn off the dark theme.
            document.body.className = '';
            this.setBooleanToLocalStorage(this.isDarkThemeKey, false);
        }
    }

    /**
     * Checks if the dark theme is set in local storage.
     * @returns True, if the dark theme is set in local storage, otherwise, false.
     */
    private isDarkTheme(): boolean {
        // Check if the dark theme is set in local storage.
        const isDarkTheme = this.getBooleanFromLocalStorage(this.isDarkThemeKey);

        // If the value is null, return the default value.
        if (isDarkTheme === null) {
            return false;
        }

        return isDarkTheme;
    }

    /**
     * Get the boolean value to Local Storage.
     * @param key The name of the boolean key.
     * @returns The value from local storage or null if not found.
     */
    private getBooleanFromLocalStorage(key: string): boolean | null {
        try {
            // Retrieve the string value from local storage.
            const storedValue = localStorage.getItem(key);

            // If the value is null or undefined, return null
            if (storedValue === null || storedValue === undefined) {
                return null;
            }

            // Convert the stored string value to a boolean.
            const booleanValue = JSON.parse(storedValue);

            // Return the boolean value.
            return booleanValue;
        }
        catch (error) {
            // Handle errors if any.
            console.error(`Error occurred while reading key '${key}' from local storage:`, error);
            return null;
        }
    }

    /**
     * Set the boolean value to Local Storage.
     * @param key The name of the boolean key.
     * @param value The boolean value to use.
     */
    private setBooleanToLocalStorage(key: string, value: boolean): void {
        try {
            // Convert the boolean value to a string before storing it.
            const stringValue = value.toString();
            localStorage.setItem(key, stringValue);
        }
        catch (error) {
            // Handle errors if any.
            console.error(`Error occurred while adding key '${key}' to local storage:`, error);
        }
    }
}
